// Header.jsx - Fixed version with proper category dropdown handling

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/oneclick-new-logo-dark.png';
import bellIcon from '../assets/bell-svgrepo-com.svg';
import defaultAvatar from '../assets/profile.png';
import "./Header.css";
import eventBus from "../eventBus";
import api from "../Api.js";

const SEARCH_PLACEHOLDERS = [
  'Search for services...',
  'AC repair...',
  'Plumbing services...',
  'Electrician near me...',
  'House cleaning...',
  'Painting services...',
  'Carpenter services...',
  'Pest control...'
];

const Header = ({ openReviewModal, showNotification, setShowNotification, notificationStep, nextNotificationStep }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [isTechnicianLoggedIn, setIsTechnicianLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [technicianName, setTechnicianName] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Category search states
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [subChildCategories, setSubChildCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedChildCategory, setSelectedChildCategory] = useState(null);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [categoryDropdownLevel, setCategoryDropdownLevel] = useState('main'); // 'main', 'sub', 'child'
  
  const categoryDropdownRefs = useRef([]);
  const navigate = useNavigate();
  const getRelatedId = (value) => (typeof value === 'string' ? value : value?._id);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated placeholder effect
  useEffect(() => {
    let timeout;
    const currentText = SEARCH_PLACEHOLDERS[placeholderIndex];
    
    if (!isDeleting) {
      if (placeholderText.length < currentText.length) {
        timeout = setTimeout(() => {
          setPlaceholderText(currentText.slice(0, placeholderText.length + 1));
        }, 80);
      } else {
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, 2000);
      }
    } else {
      if (placeholderText.length > 0) {
        timeout = setTimeout(() => {
          setPlaceholderText(placeholderText.slice(0, -1));
        }, 40);
      } else {
        setIsDeleting(false);
        setPlaceholderIndex((prev) => (prev + 1) % SEARCH_PLACEHOLDERS.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [placeholderText, isDeleting, placeholderIndex]);

  // Fetch categories for search
  useEffect(() => {
    fetchSearchCategories();
  }, []);

  const fetchSearchCategories = async () => {
    try {
      setLoadingCategories(true);
      
      const [
        categoriesRes,
        subCategoriesRes,
        childCategoriesRes,
        subChildCategoriesRes
      ] = await Promise.all([
        api.get('/api/web/categories'),
        api.get('/api/web/sub-categories'),
        api.get('/api/web/child-categories'),
        api.get('/api/web/sub-child-categories')
      ]);

      const activeCategories = categoriesRes.data.data.filter(cat => cat.status === true);
      setCategories(activeCategories);
      
      const activeSubCategories = subCategoriesRes.data.data.filter(sub => sub.status === true);
      setSubCategories(activeSubCategories);
      
      const activeChildCategories = childCategoriesRes.data.data.filter(child => child.status === true);
      setChildCategories(activeChildCategories);
      
      const activeSubChildCategories = subChildCategoriesRes.data.data.filter(subChild => subChild.status === true);
      setSubChildCategories(activeSubChildCategories);

      setSearchError(null);
    } catch (err) {
      console.error('Error fetching category data for search:', err);
      setSearchError('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Get sub-categories for a specific category
  const getSubCategoriesByCategory = (categoryId) => {
    return subCategories.filter(sub => getRelatedId(sub.categoryId) === categoryId);
  };

  // Get child categories for a specific sub-category
  const getChildCategoriesBySubCategory = (subCategoryId) => {
    return childCategories.filter(child => getRelatedId(child.subCategoryId) === subCategoryId);
  };

  // Get sub-child categories for a specific child category
  const getSubChildCategoriesByChild = (childCategoryId) => {
    return subChildCategories.filter(subChild => getRelatedId(subChild.childCategoryId) === childCategoryId);
  };

  // FIXED: Handle category selection - properly shows sub-categories
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);
    setSelectedChildCategory(null);
    setIsCategoryDropdownOpen(true);
    setCategoryDropdownLevel('sub');
  };

  // FIXED: Handle sub-category selection
  const handleSubCategorySelect = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setSelectedChildCategory(null);
    
    const childCats = getChildCategoriesBySubCategory(subCategory._id);
    
    if (childCats.length > 0) {
      // Show child categories
      setCategoryDropdownLevel('child');
    } else {
      // No child categories, redirect to category page with sub-category
      setIsCategoryDropdownOpen(false);
      setCategoryDropdownLevel('main');
      
      // Navigate to category page
      navigateToCategoryPage(subCategory);
    }
  };

  // Handle child category selection - redirect immediately
  const handleChildCategorySelect = (childCategory) => {
    setSelectedChildCategory(childCategory);
    setIsCategoryDropdownOpen(false);
    setCategoryDropdownLevel('main');
    
    // Navigate to category page with child category
    navigateToCategoryPage(selectedSubCategory, childCategory);
  };

  // Navigate to category page
  const navigateToCategoryPage = (subCategory, childCategory = null) => {
    const categoryId = selectedCategory?._id;
    
    if (childCategory) {
      // Navigate with child category
      navigate(`/category/${categoryId}`, {
        state: {
          category: selectedCategory,
          subCategory: subCategory,
          childCategory: childCategory,
          childCategories: [childCategory],
          subChildCategories: getSubChildCategoriesByChild(childCategory._id)
        }
      });
    } else if (subCategory) {
      // Navigate with sub-category
      navigate(`/category/${categoryId}`, {
        state: {
          category: selectedCategory,
          subCategory: subCategory,
          childCategories: getChildCategoriesBySubCategory(subCategory._id),
          subChildCategories: subChildCategories
        }
      });
    }
    
    // Reset search
    resetSearch();
  };

  // Handle search button click or form submit
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();

    const query = searchQuery.trim();
    if (query) {
      const searchParams = new URLSearchParams({ search: query });
      if (selectedCategory?._id) searchParams.set('categoryId', selectedCategory._id);
      if (selectedSubCategory?._id) searchParams.set('subCategoryId', selectedSubCategory._id);
      if (selectedChildCategory?._id) searchParams.set('childCategoryId', selectedChildCategory._id);

      navigate(`/services?${searchParams.toString()}`, {
        state: {
          categoryScope: {
            category: selectedCategory,
            subCategory: selectedSubCategory,
            childCategory: selectedChildCategory,
            subCategories,
            childCategories,
            subChildCategories
          }
        }
      });
      resetSearch();
      return;
    }
    
    // If a sub-category is selected, navigate to it
    if (selectedSubCategory) {
      navigateToCategoryPage(selectedSubCategory, selectedChildCategory);
      return;
    }
    
    // If a category is selected but no sub-category, show sub-categories
    if (selectedCategory && !selectedSubCategory) {
      setIsCategoryDropdownOpen(true);
      setCategoryDropdownLevel('sub');
      return;
    }
    
  };

  // Reset search state
  const resetSearch = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedChildCategory(null);
    setIsCategoryDropdownOpen(false);
    setCategoryDropdownLevel('main');
  };

  // FIXED: Go back to previous level in dropdown
  const goBackToMainCategories = () => {
    setCategoryDropdownLevel('main');
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedChildCategory(null);
    setIsCategoryDropdownOpen(true);
  };

  const goBackToSubCategories = () => {
    setCategoryDropdownLevel('sub');
    setSelectedChildCategory(null);
  };

  // FIXED: Toggle category dropdown - properly handles opening/closing
  const toggleCategoryDropdown = () => {
    if (isCategoryDropdownOpen) {
      // Close everything
      setIsCategoryDropdownOpen(false);
      setCategoryDropdownLevel('main');
      // Reset selections when closing
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setSelectedChildCategory(null);
    } else {
      // Open main categories
      setIsCategoryDropdownOpen(true);
      setCategoryDropdownLevel('main');
      // Reset selections when opening from scratch
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setSelectedChildCategory(null);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedSearch = categoryDropdownRefs.current.some(
        (searchRef) => searchRef?.contains(event.target)
      );
      if (!clickedSearch) {
        setIsCategoryDropdownOpen(false);
        setCategoryDropdownLevel('main');
        // Don't reset selections here to maintain state
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const openProfileModal = () => {
      setIsProfileDropdownOpen(true);
      setShowNotification(false);
    };
    eventBus.on('openProfileModal', openProfileModal);
    return () => eventBus.off('openProfileModal', openProfileModal);
  }, [setShowNotification]);

  useEffect(() => {
    if (!isProfileDropdownOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsProfileDropdownOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isProfileDropdownOpen]);

  // Check both user and technician authentication
  useEffect(() => {
    const checkAuth = () => {
      const userToken = localStorage.getItem('user_token') || localStorage.getItem('auth_token');
      const userInfo = localStorage.getItem('user_info');
      let userLoggedIn = false;
      let userNameTemp = '';

      if (userToken && userInfo) {
        try {
          const user = JSON.parse(userInfo);
          userLoggedIn = true;
          userNameTemp = user.user_name || user.name || 'User';
        } catch (e) {}
      }
      setIsUserLoggedIn(userLoggedIn);
      setUserName(userNameTemp);

      const techToken = localStorage.getItem('technician_token') || localStorage.getItem('auth_token');
      const techInfo = localStorage.getItem('technician_info');
      let techLoggedIn = false;
      let techNameTemp = '';

      if (techToken && techInfo) {
        try {
          const tech = JSON.parse(techInfo);
          techLoggedIn = true;
          techNameTemp = tech.name || 'Technician';
        } catch (e) {}
      }
      setIsTechnicianLoggedIn(techLoggedIn);
      setTechnicianName(techNameTemp);
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  useEffect(() => {
    // Listen for login events
    const handleLoginEvent = (data) => {
      console.log('Login event received in Header:', data);
      
      setIsUserLoggedIn(false);
      setIsTechnicianLoggedIn(false);
      setUserName('');
      setTechnicianName('');
      
      if (data.userType === 'user') {
        setIsUserLoggedIn(true);
        setUserName(data.userInfo?.user_name || data.userInfo?.name || 'User');
      } else if (data.userType === 'tech' || data.userType === 'technician') {
        setIsTechnicianLoggedIn(true);
        setTechnicianName(data.userInfo?.name || data.userInfo?.user_name || 'Technician');
      }
      
      setIsProfileDropdownOpen(false);
      setShowNotification(false);
    };

    const handleLogoutEvent = () => {
      console.log('Logout event received in Header');
      setIsUserLoggedIn(false);
      setIsTechnicianLoggedIn(false);
      setUserName('');
      setTechnicianName('');
    };

    eventBus.on('userLoggedIn', handleLoginEvent);
    eventBus.on('userLoggedOut', handleLogoutEvent);

    return () => {
      eventBus.off('userLoggedIn', handleLoginEvent);
      eventBus.off('userLoggedOut', handleLogoutEvent);
    };
  }, [setShowNotification]);

  // ===== FEEDBACK NOTIFICATION LOGIC (shared with UserProfile) =====

  // Helper: dedupe by unique userId + technicianId pair.
  // Keeps the latest; prefers 'submitted' over 'pending' if both exist.
  const dedupeByTechUserPair = (items) => {
    const map = new Map();
    for (const n of items) {
      const techId = n.metadata?.technicianId || n.technician?.technician_id || n._id;
      const userId = n.userId || 'unknown';
      const key = `${userId}::${techId}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, n);
      } else {
        const existingSubmitted = existing.feedback_status === 'submitted';
        const currentSubmitted = n.feedback_status === 'submitted';
        if (currentSubmitted && !existingSubmitted) {
          map.set(key, n);
        } else if (currentSubmitted === existingSubmitted) {
          if (new Date(n.createdAt) > new Date(existing.createdAt)) {
            map.set(key, n);
          }
        }
      }
    }
    return Array.from(map.values());
  };

  // Pending feedback only — unique by userId + technicianId
  const pendingNotifications = useMemo(() => {
    const pending = notifications.filter((n) => n.feedback_status === 'pending');
    const sorted = [...pending].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return dedupeByTechUserPair(sorted);
  }, [notifications]);

  // Submitted feedback — unique pairs (used for counts / "Show All")
  const submittedNotifications = useMemo(() => {
    const submitted = notifications.filter((n) => n.feedback_status === 'submitted');
    const sorted = [...submitted].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return dedupeByTechUserPair(sorted);
  }, [notifications]);

  // Deduped "all" list (pending first, then submitted)
  const allUniqueNotifications = useMemo(() => {
    return [...pendingNotifications, ...submittedNotifications];
  }, [pendingNotifications, submittedNotifications]);

  // What to render in the dropdown
  const visibleNotifications = showAllNotifications
    ? allUniqueNotifications
    : pendingNotifications;

  // Unread badge = unique pending pairs
  const uniquePendingCount = pendingNotifications.length;

  // Fetch notifications function
  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      
      const token = localStorage.getItem('user_token') || localStorage.getItem('auth_token');
      
      if (!token) {
        setNotifications([]);
        setLoadingNotifications(false);
        return;
      }

      const response = await api.get('/api/web/user/feedback-notifications?page=1&limit=50', {
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = response.data;
      
      if (!data?.success || !Array.isArray(data.data)) {
        throw new Error(data?.message || 'Unable to load feedback notifications.');
      }
      setNotifications(data.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    const handleNotificationLogin = ({ userType } = {}) => {
      if (userType === 'user') fetchNotifications();
    };
    const clearNotifications = () => {
      setNotifications([]);
    };

    eventBus.on('userLoggedIn', handleNotificationLogin);
    eventBus.on('userLoggedOut', clearNotifications);
    eventBus.on('feedbackUpdated', fetchNotifications);
    fetchNotifications();

    return () => {
      eventBus.off('userLoggedIn', handleNotificationLogin);
      eventBus.off('userLoggedOut', clearNotifications);
      eventBus.off('feedbackUpdated', fetchNotifications);
    };
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleNotification = () => {
    setShowNotification(!showNotification);
  };
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen((isOpen) => !isOpen);
    setShowNotification(false);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_token');
    localStorage.removeItem('technician_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('technician_info');
    setIsUserLoggedIn(false);
    setIsTechnicianLoggedIn(false);
    
    eventBus.emit('userLoggedOut');
    
    navigate('/login');
    window.dispatchEvent(new Event('storage'));
  };

  const handleRoleSwitch = (event, targetRole) => {
    event.preventDefault();
    const hasActiveSession = isUserLoggedIn || isTechnicianLoggedIn;

    if (hasActiveSession && !window.confirm('You will be logged out of your current account. Continue?')) {
      return;
    }

    if (hasActiveSession) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_token');
      localStorage.removeItem('technician_token');
      localStorage.removeItem('user_info');
      localStorage.removeItem('technician_info');
      setIsUserLoggedIn(false);
      setIsTechnicianLoggedIn(false);
      setUserName('');
      setTechnicianName('');
      eventBus.emit('userLoggedOut');
      window.dispatchEvent(new Event('storage'));
    }

    setIsProfileDropdownOpen(false);
    setShowNotification(false);
    setIsSidebarOpen(false);
    navigate(`/login?tab=${targetRole}`);
  };

  const getAvatarInitial = () => {
    if (isUserLoggedIn) return userName.charAt(0).toUpperCase();
    if (isTechnicianLoggedIn) return technicianName.charAt(0).toUpperCase();
    return '';
  };

  const getDisplayName = () => {
    if (isUserLoggedIn) return userName.split(' ')[0];
    if (isTechnicianLoggedIn) return technicianName.split(' ')[0];
    return '';
  };

  // Render notification item — shows technician name & pic, unique per technician/user pair
  const renderNotificationItem = (notification, index) => {
    const notificationId = notification._id;
    const technicianId =
      notification.metadata?.technicianId ||
      notification.technician?.technician_id ||
      null;
    const viewLogId = notification.metadata?.viewId || null;
    const techName =
      notification.technician?.name ||
      notification.metadata?.technicianName ||
      'Technician';
    const techPic = notification.technician?.profile_pic || defaultAvatar;
    const message = notification.message || 'Please give feedback for your recent service';
    const createdAt = notification.createdAt;
    const isPending = notification.feedback_status === 'pending';

    return (
      <div
        key={`${notification.userId}-${technicianId}-${notificationId}`}
        className="nexusNotiItem"
      >
        <div className="nexusNotiTechRow">
          <img
            src={techPic}
            alt={techName}
            className="nexusNotiTechAvatar"
            onError={(e) => {
              e.currentTarget.src = defaultAvatar;
            }}
          />
          <div className="nexusNotiTechMeta">
            <p className="nexusNotiTechName">{techName}</p>
            <span className="nexusNotiTechSub">{message}</span>
          </div>
        </div>

        <div className="nexusNotiContent">
          {createdAt && (
            <span className="nexusNotiTime">
              {new Date(createdAt).toLocaleDateString()}
            </span>
          )}
          {!isPending && (
            <span className="nexusNotiReadBadge">✓ Reviewed</span>
          )}
        </div>

        {isPending ? (
          <div className="nexusNotiActions">
            <button
              className="nexusNotiYesBtn"
              onClick={() => {
                if (openReviewModal) {
                  openReviewModal({
                    notification: notification,
                    notificationId: notificationId,
                    technicianId: technicianId,
                    viewLogId: viewLogId,
                    technicianName: techName,
                  });
                  setShowNotification(false);
                }
              }}
            >
              Yes, Rate Now
            </button>
            <button
              className="nexusNotiNoBtn"
              onClick={() => {
                // Remove this pair from local pending view
                setNotifications((prev) =>
                  prev.filter((n) => {
                    const nTechId =
                      n.metadata?.technicianId || n.technician?.technician_id;
                    return !(
                      n.userId === notification.userId &&
                      nTechId === technicianId
                    );
                  })
                );
                if (nextNotificationStep) {
                  nextNotificationStep(6);
                }
              }}
            >
              Not Now
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  // Shared notification dropdown renderer (desktop + mobile)
  const renderNotificationDropdown = () => {
    if (loadingNotifications) {
      return <div className="nexusNotiLoading">Loading notifications...</div>;
    }

    if (pendingNotifications.length === 0 && submittedNotifications.length === 0) {
      return (
        <div className="nexusNotiEmpty">
          <p>No notifications</p>
          <span className="nexusNotiEmptySub">Check back later</span>
        </div>
      );
    }

    return (
      <>
        <div className="nexusNotiHeader">
          <div className="nexusNotiHeaderTop">
            <span className="nexusNotiTitle">Feedback Requests</span>
            <span className="nexusNotiCount">
              {uniquePendingCount} pending • {submittedNotifications.length} reviewed
            </span>
          </div>
          <button
            type="button"
            className="nexusNotiToggleBtn"
            onClick={() => setShowAllNotifications((v) => !v)}
          >
            {showAllNotifications
              ? 'Show pending only'
              : `Show all (${allUniqueNotifications.length})`}
          </button>
        </div>

        <div className="nexusNotiList">
          {visibleNotifications.length > 0 ? (
            visibleNotifications.map((notification, index) =>
              renderNotificationItem(notification, index)
            )
          ) : (
            <div className="nexusNotiEmpty">
              <p>You're all caught up</p>
              <span className="nexusNotiEmptySub">No pending reviews</span>
            </div>
          )}
        </div>
      </>
    );
  };

  // FIXED: Search bar with category dropdown - ensures proper rendering
  const renderSearchBar = (index) => (
    <div
      className="nexusSearchWrapper"
      ref={(element) => {
        categoryDropdownRefs.current[index] = element;
      }}
    >
      <form onSubmit={handleSearchSubmit} className="nexusSearchForm">
        {/* Category Dropdown Button */}
        <div className="nexusCategoryDropdownWrapper">
          <button 
            type="button" 
            className={`nexusCategoryDropdownBtn ${selectedCategory ? 'active' : ''}`}
            onClick={toggleCategoryDropdown}
          >
            {selectedCategory ? selectedCategory.categoryName : 'All Categories'}
            <span className="nexusDropdownArrow">⌵</span>
          </button>
          
          {/* Category Dropdown Menu */}
          {isCategoryDropdownOpen && (
            <div className="nexusCategoryDropdownMenu">
              {loadingCategories ? (
                <div className="nexusDropdownLoading">Loading categories...</div>
              ) : searchError ? (
                <div className="nexusDropdownError">{searchError}</div>
              ) : (
                <>
                  {/* Main Category List */}
                  {categoryDropdownLevel === 'main' && (
                    <div className="nexusCategoryList">
                      <div className="nexusCategoryHeader">
                        <span>Select a Category</span>
                      </div>
                      {categories.map((category) => {
                        const subCats = getSubCategoriesByCategory(category._id);
                        return (
                          <div
                            key={category._id}
                            className={`nexusCategoryItem ${selectedCategory?._id === category._id ? 'selected' : ''}`}
                            onClick={() => handleCategorySelect(category)}
                          >
                            <div className="nexusCategoryIcon">
                              <img 
                                src={category.icon || 'https://png.pngtree.com/png-vector/20221125/ourmid/pngtree-no-image-available-icon-flatvector-illustration-picture-coming-creative-vector-png-image_40968940.jpg'} 
                                alt={category.categoryName}
                                onError={(e) => {
                                  e.target.src = 'https://png.pngtree.com/png-vector/20221125/ourmid/pngtree-no-image-available-icon-flatvector-illustration-picture-coming-creative-vector-png-image_40968940.jpg';
                                }}
                              />
                            </div>
                            <span>{category.categoryName}</span>
                            <span className="nexusCategoryCount">{subCats.length}</span>
                            <span className="nexusCategoryArrow">→</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* FIXED: Sub-Category List - Now properly visible */}
                  {categoryDropdownLevel === 'sub' && selectedCategory && (
                    <div className="nexusSubCategoryList">
                      <div className="nexusCategoryHeader">
                        <button 
                          type="button"
                          className="nexusBackBtn"
                          onClick={goBackToMainCategories}
                        >
                          ← Back
                        </button>
                        <span className="nexusCategoryTitle">{selectedCategory.categoryName}</span>
                      </div>
                      {getSubCategoriesByCategory(selectedCategory._id).length > 0 ? (
                        getSubCategoriesByCategory(selectedCategory._id).map((sub) => {
                          const childCats = getChildCategoriesBySubCategory(sub._id);
                          return (
                            <div
                              key={sub._id}
                              className={`nexusCategoryItem ${selectedSubCategory?._id === sub._id ? 'selected' : ''}`}
                              onClick={() => handleSubCategorySelect(sub)}
                            >
                              <div className="nexusCategoryIcon">
                                <span>📋</span>
                              </div>
                              <span>{sub.subCategoryName}</span>
                              <span className="nexusCategoryCount">{childCats.length}</span>
                              {childCats.length > 0 && (
                                <span className="nexusCategoryArrow">→</span>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="nexusNoItems">No sub-categories available</div>
                      )}
                    </div>
                  )}

                  {/* Child Category List */}
                  {categoryDropdownLevel === 'child' && selectedSubCategory && (
                    <div className="nexusChildCategoryList">
                      <div className="nexusCategoryHeader">
                        <button 
                          type="button"
                          className="nexusBackBtn"
                          onClick={goBackToSubCategories}
                        >
                          ← Back
                        </button>
                        <span className="nexusCategoryTitle">{selectedSubCategory.subCategoryName}</span>
                      </div>
                      {getChildCategoriesBySubCategory(selectedSubCategory._id).length > 0 ? (
                        getChildCategoriesBySubCategory(selectedSubCategory._id).map((child) => (
                          <div
                            key={child._id}
                            className={`nexusCategoryItem ${selectedChildCategory?._id === child._id ? 'selected' : ''}`}
                            onClick={() => handleChildCategorySelect(child)}
                          >
                            <div className="nexusCategoryIcon">
                              <span>🔧</span>
                            </div>
                            <span>{child.childCategoryName}</span>
                            <span className="nexusCategoryArrow">→</span>
                          </div>
                        ))
                      ) : (
                        <div className="nexusNoItems">No child categories available</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Search Input */}
        <div className="nexusSearchInputWrapper">
          <div className="nexusSearchIcon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="M21 21l-4.35-4.35"></path>
            </svg>
          </div>
          <input
            type="text"
            className="nexusSearchInput"
            placeholder={selectedSubCategory ? `Search in ${selectedSubCategory.subCategoryName}...` : 
                         selectedCategory ? `Search in ${selectedCategory.categoryName}...` : 
                         placeholderText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              setPlaceholderText('');
              setIsDeleting(false);
            }}
            onBlur={() => {
              if (!searchQuery) {
                setPlaceholderText('');
                setIsDeleting(false);
                setPlaceholderIndex(0);
              }
            }}
          />
          {searchQuery && (
            <button type="button" className="nexusSearchClear" onClick={() => setSearchQuery('')}>
              ×
            </button>
          )}
        </div>
        
        <button type="submit" className="nexusSearchButton">
          Search
        </button>
      </form>
    </div>
  );

  return (
    <>
      <header className={`nexusHeader ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nexusHeaderContainer">
          {/* Desktop Layout - Single Row */}
          <div className="nexusDesktopLayout">
            {/* Logo */}
            <div className="nexusLogo">
              <Link to="/">
                <img src={logo} alt="OneClick" />
              </Link>
            </div>

            {/* Search Bar with Category Dropdown - Desktop */}
            {renderSearchBar(0)}

            {/* Right CTA - Desktop */}
            <div className="nexusCta">
              {/* Notification Bell */}
              <div className="nexusNotification" onClick={toggleNotification}>
                <img src={bellIcon} alt="Notifications" width="28" height="28" />
                {uniquePendingCount > 0 && (
                  <span className="nexusNotiBadge">{uniquePendingCount}</span>
                )}
                <span className="nexusNotiDot" style={{ display: uniquePendingCount > 0 ? 'block' : 'none' }}></span>
                <div className={`nexusNotiDrop ${showNotification ? 'show' : ''}`}>
                  {renderNotificationDropdown()}
                </div>
              </div>

              {/* Profile / Login */}
              <div className="nexusProfileWrapper">
                {(isUserLoggedIn || isTechnicianLoggedIn) ? (
                  <>
                    <button
                      type="button"
                      className="nexusProfileIcon"
                      onClick={toggleProfileDropdown}
                      aria-haspopup="dialog"
                      aria-expanded={isProfileDropdownOpen}
                    >
                      <div className="nexusAvatar">
                        {getAvatarInitial()}
                      </div>
                      <span className="nexusUserName">{getDisplayName()}</span>
                      <span className="nexusDropdownArrow">⌵</span>
                    </button>
                    <div className="nexusProfileDropdown">
                      {isUserLoggedIn && (
                        <Link to="/profile" className="nexusDropdownItem" onClick={() => setIsProfileDropdownOpen(false)}>
                          <span className="nexusDropdownIcon">👤</span> My Profile (User)
                        </Link>
                      )}
                      {isTechnicianLoggedIn && (
                        <Link to="/tech-dashboard" className="nexusDropdownItem" onClick={() => setIsProfileDropdownOpen(false)}>
                          <span className="nexusDropdownIcon">🔧</span> Technician Dashboard
                        </Link>
                      )}
                      {isTechnicianLoggedIn && !isUserLoggedIn && (
                        <Link to="/login?tab=user" className="nexusDropdownItem" onClick={(event) => handleRoleSwitch(event, 'user')}>
                          <span className="nexusDropdownIcon">👤</span> Switch to User Login
                        </Link>
                      )}
                      {isUserLoggedIn && !isTechnicianLoggedIn && (
                        <Link to="/login?tab=tech" className="nexusDropdownItem" onClick={(event) => handleRoleSwitch(event, 'tech')}>
                          <span className="nexusDropdownIcon">🔧</span> Switch to Technician Login
                        </Link>
                      )}
                      <button onClick={handleLogout} className="nexusDropdownItem nexusDropdownLogout">
                        <span className="nexusDropdownIcon">🚪</span> Logout All
                      </button>
                    </div>
                  </>
                ) : (
                  <Link to="/login" className="nexusLoginBtn">
                    <div className="nexusLoginIcon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <span>Login</span>
                  </Link>
                )}
              </div>

              {/* Become a Technician Button */}
              {!isTechnicianLoggedIn && (
                <Link to="/login?tab=tech" className="nexusTechBtnLink" onClick={(event) => handleRoleSwitch(event, 'tech')}>
                  <button className="nexusTechBtn">
                    <span>Become a Technician</span>
                  </button>
                                </Link>
              )}
            </div>
          </div>

          {/* Mobile Layout - Two Rows */}
          <div className="nexusMobileLayout">
            {/* Top Row: Logo, Profile, Notification, Hamburger */}
            <div className="nexusTopRow">
              <div className="nexusLogo">
                <Link to="/">
                  <img src={logo} alt="OneClick" />
                </Link>
              </div>

              <div className="nexusMobileControls">
                {/* Notification Bell - Mobile */}
                <div className="nexusNotificationMobile" onClick={toggleNotification}>
                  <img src={bellIcon} alt="Notifications" width="24" height="24" />
                  {uniquePendingCount > 0 && (
                    <span className="nexusNotiBadgeMobile">{uniquePendingCount}</span>
                  )}
                  <span className="nexusNotiDotMobile" style={{ display: uniquePendingCount > 0 ? 'block' : 'none' }}></span>
                  <div className={`nexusNotiDrop ${showNotification ? 'show' : ''}`}>
                    {renderNotificationDropdown()}
                  </div>
                </div>

                {/* Profile / Login - Mobile */}
                <div className="nexusProfileWrapper">
                  {(isUserLoggedIn || isTechnicianLoggedIn) ? (
                    <>
                      <button
                        type="button"
                        className="nexusProfileIcon"
                        onClick={toggleProfileDropdown}
                        aria-haspopup="dialog"
                        aria-expanded={isProfileDropdownOpen}
                      >
                        <div className="nexusAvatar">
                          {getAvatarInitial()}
                        </div>
                        <span className="nexusUserName">{getDisplayName()}</span>
                        <span className="nexusDropdownArrow">⌵</span>
                      </button>
                      <div className="nexusProfileDropdown">
                        {isUserLoggedIn && (
                          <Link to="/profile" className="nexusDropdownItem" onClick={() => setIsProfileDropdownOpen(false)}>
                            <span className="nexusDropdownIcon">👤</span> My Profile (User)
                          </Link>
                        )}
                        {isTechnicianLoggedIn && (
                          <Link to="/tech-dashboard" className="nexusDropdownItem" onClick={() => setIsProfileDropdownOpen(false)}>
                            <span className="nexusDropdownIcon">🔧</span> Technician Dashboard
                          </Link>
                        )}
                        {isTechnicianLoggedIn && !isUserLoggedIn && (
                          <Link to="/login?tab=user" className="nexusDropdownItem" onClick={(event) => handleRoleSwitch(event, 'user')}>
                            <span className="nexusDropdownIcon">👤</span> Switch to User Login
                          </Link>
                        )}
                        {isUserLoggedIn && !isTechnicianLoggedIn && (
                          <Link to="/login?tab=tech" className="nexusDropdownItem" onClick={(event) => handleRoleSwitch(event, 'tech')}>
                            <span className="nexusDropdownIcon">🔧</span> Switch to Technician Login
                          </Link>
                        )}
                        <button onClick={handleLogout} className="nexusDropdownItem nexusDropdownLogout">
                          <span className="nexusDropdownIcon">🚪</span> Logout All
                        </button>
                      </div>
                    </>
                  ) : (
                    <Link to="/login" className="nexusLoginBtn">
                      <div className="nexusLoginIcon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <span>Login</span>
                    </Link>
                  )}
                </div>

                {/* Become a Technician Button - Mobile */}
                {!isTechnicianLoggedIn && (
                  <Link to="/login?tab=tech" className="nexusTechBtnLink" onClick={(event) => handleRoleSwitch(event, 'tech')}>
                    <button className="nexusTechBtn">
                      <span>Become a Technician</span>
                    </button>
                  </Link>
                )}

                {/* Hamburger for Mobile */}
                <div className="nexusHamburger" onClick={toggleSidebar}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>

            {/* Bottom Row: Search Bar with Category Dropdown - Mobile */}
            <div className="nexusBottomRow">
              {renderSearchBar(1)}
            </div>
          </div>
        </div>

        {/* Mobile Sidebar */}
        <div className={`nexusSidebar ${isSidebarOpen ? 'active' : ''}`}>
          <div className="nexusSidebarHeader">
            <span className="nexusCloseBtn" onClick={toggleSidebar}>×</span>
          </div>
          <ul className="nexusSidebarNav">
            {(isUserLoggedIn || isTechnicianLoggedIn) ? (
              <>
                {isUserLoggedIn && <li><Link to="/profile" onClick={toggleSidebar}>My Profile (User)</Link></li>}
                {isTechnicianLoggedIn && <li><Link to="/tech-dashboard" onClick={toggleSidebar}>Technician Dashboard</Link></li>}
                {isTechnicianLoggedIn && !isUserLoggedIn && (
                  <li><Link to="/login?tab=user" onClick={(event) => handleRoleSwitch(event, 'user')}>Switch to User Login</Link></li>
                )}
                {isUserLoggedIn && !isTechnicianLoggedIn && (
                  <li><Link to="/login?tab=tech" onClick={(event) => handleRoleSwitch(event, 'tech')}>Switch to Technician Login</Link></li>
                )}
                <li><button onClick={() => { handleLogout(); toggleSidebar(); }} className="nexusSidebarLogout">Logout All</button></li>
              </>
            ) : (
              <li><Link to="/login" onClick={toggleSidebar}>Login</Link></li>
            )}
          </ul>
        </div>
        <div className={`nexusOverlay ${isSidebarOpen ? 'active' : ''}`} onClick={toggleSidebar}></div>
      </header>
      {isProfileDropdownOpen && createPortal(
        <div
          className="nexusProfileModalOverlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsProfileDropdownOpen(false);
          }}
        >
          <section
            className="nexusProfileModal"
            role="dialog"
            aria-modal="true"
            aria-label="Account menu"
          >
            <button
              type="button"
              className="nexusProfileModalClose"
              aria-label="Close account menu"
              onClick={() => setIsProfileDropdownOpen(false)}
            >
              ×
            </button>
            <h2>Account</h2>
            {(isUserLoggedIn || isTechnicianLoggedIn) ? (
              <>
                {isUserLoggedIn && (
                  <Link to="/profile" className="nexusDropdownItem" onClick={() => setIsProfileDropdownOpen(false)}>
                    <span className="nexusDropdownIcon">👤</span> My Profile (User)
                  </Link>
                )}
                {isTechnicianLoggedIn && (
                  <Link to="/tech-dashboard" className="nexusDropdownItem" onClick={() => setIsProfileDropdownOpen(false)}>
                    <span className="nexusDropdownIcon">🔧</span> Technician Dashboard
                  </Link>
                )}
                {isTechnicianLoggedIn && !isUserLoggedIn && (
                  <Link to="/login?tab=user" className="nexusDropdownItem" onClick={(event) => handleRoleSwitch(event, 'user')}>
                    <span className="nexusDropdownIcon">👤</span> Switch to User Login
                  </Link>
                )}
                {isUserLoggedIn && !isTechnicianLoggedIn && (
                  <Link to="/login?tab=tech" className="nexusDropdownItem" onClick={(event) => handleRoleSwitch(event, 'tech')}>
                    <span className="nexusDropdownIcon">🔧</span> Switch to Technician Login
                  </Link>
                )}
                <button
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    handleLogout();
                  }}
                  className="nexusDropdownItem nexusDropdownLogout"
                >
                  <span className="nexusDropdownIcon">🚪</span> Logout All
                </button>
              </>
            ) : (
              <Link to="/login" className="nexusDropdownItem" onClick={() => setIsProfileDropdownOpen(false)}>
                <span className="nexusDropdownIcon">👤</span> Login
              </Link>
            )}
          </section>
        </div>,
        document.body
      )}
    </>
  );
};

export default Header;